import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Home, Compass, Activity, Shield } from "lucide-react";
import { Avatar } from "./radix/Avatar";
import { Tabs } from "./radix/Tabs";
import { SearchModal } from "./SearchModal";
import { authService } from "../services/auth";
import { useSignInModal } from "../context/SignInModalContext";
import "./AppShell.css";

interface AppShellProps {
  userInfo: any;
  children: React.ReactNode;
  onCreatePoll?: () => void;
  showTabs?: boolean;
}

export const AppShell: React.FC<AppShellProps> = ({
  userInfo,
  children,
  onCreatePoll,
  showTabs = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const { showSignInModal } = useSignInModal();

  const getUserInitials = () => {
    if (!userInfo) return "U";

    const givenName = userInfo.given_name || userInfo.name?.split(" ")[0];
    const familyName = userInfo.family_name || userInfo.name?.split(" ")[1];

    if (givenName && familyName) {
      return `${givenName[0]}${familyName[0]}`.toUpperCase();
    }

    if (givenName) {
      return givenName[0].toUpperCase();
    }

    if (userInfo.email) {
      return userInfo.email[0].toUpperCase();
    }

    return "U";
  };

  const getActiveTab = (): "feed" | "explore" | "activity" | null => {
    if (location.pathname === "/explore") return "explore";
    if (location.pathname === "/activity") return "activity";
    if (location.pathname.startsWith("/feed")) return "feed";
    return null;
  };

  const activeTab = getActiveTab();
  const showCreateButton = !!onCreatePoll;
  const isAdmin = authService.isAdmin();

  const handleCreatePoll = () => {
    if (!userInfo) {
      showSignInModal();
    } else if (onCreatePoll) {
      onCreatePoll();
    }
  };

  const handleAvatarClick = () => {
    if (!userInfo) {
      showSignInModal();
    } else {
      navigate("/account");
    }
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-brand" onClick={() => navigate("/feed")}>
          <img src="/logo.svg" alt="Opoko logo" className="header-logo" />
          <h1>Opoko</h1>
        </div>
        <div className="header-actions">
          <button
            className="icon-btn search-btn"
            onClick={() => setShowSearchModal(true)}
            aria-label="Search polls"
            title="Search polls"
          >
            <Search size={20} />
          </button>
          {isAdmin && (
            <button
              className="icon-btn admin-btn"
              onClick={() => navigate("/admin")}
              aria-label="Admin Console"
              title="Admin Console"
            >
              <Shield size={20} />
            </button>
          )}
          {showCreateButton && (
            <button
              className="btn btn-primary create-poll-btn"
              onClick={handleCreatePoll}
            >
              + Create Poll
            </button>
          )}
          <Avatar
            src={userInfo?.picture}
            fallback={getUserInitials()}
            className="avatar-btn"
            onClick={handleAvatarClick}
            title="My Account"
          />
        </div>
      </header>

      <SearchModal open={showSearchModal} onOpenChange={setShowSearchModal} />

      <main className="app-content">{children}</main>

      {showTabs && (
        <Tabs.Root
          value={activeTab || "feed"}
          onValueChange={(value) => navigate(`/${value}`)}
          className="app-nav"
        >
          <Tabs.List className="app-nav-list">
            <Tabs.Trigger value="feed" className="tab-button">
              <Home size={18} />
              <span>Feed</span>
            </Tabs.Trigger>
            <Tabs.Trigger value="explore" className="tab-button">
              <Compass size={18} />
              <span>Explore</span>
            </Tabs.Trigger>
            <Tabs.Trigger value="activity" className="tab-button">
              <Activity size={18} />
              <span>Activity</span>
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>
      )}
    </div>
  );
};
